/**
 * BulletinRecordsView — /catalog-entry/bulletin/:vendor/:doc_no/records  레코드 페이지
 *
 * 한 편의 assemble 결과(assembled/<번호>.jsonl)를 줄 단위로 본다. 한 편 최대 2,081줄이라 ③ 안에 못 끼워 별도 페이지.
 * assemble_error(사람이 적는 버그 칸)는 레코드를 보며 적는 것이라 ③ 에서 여기로 옮겼다(현민 2026-09-29).
 *
 * 분해: bulletin-records/types.ts(상태) · bulletin-records/render.ts(그리기, 순수) · 이 파일(핸들러 + 로드 + 부분 재렌더).
 * 주소창 값(vendor · doc_no)은 밖에서 온 값 — 생성자 한 곳에서 assert 로 거른다. 어긋나면 라우터의 RouteErrorView 가 받는다.
 */
import { SolaView, htmlSolaWithInstance } from '@hm-spt/sola';
import * as api from '../api/client';
import { assert } from '../lib/assert';
import { E_RECORD_TYPE_FILTERS } from './bulletin-records/types';
import type { BulletinRecordsData, ERecordTypeFilter } from './bulletin-records/types';
import { renderAssembleError, renderRecords, renderRecordsHead, renderTypeTabs } from './bulletin-records/render';

/** 주소창의 :vendor 는 밖에서 온 값 — 셋 중 하나인가 */
function isVendor(raw: string): raw is api.EVendor {
  return api.E_VENDORS.some((v) => v === raw);
}

function isRecordTypeFilter(raw: string | undefined): raw is ERecordTypeFilter {
  return E_RECORD_TYPE_FILTERS.some((f) => f === raw);
}

export class BulletinRecordsView extends SolaView<BulletinRecordsData> {
  override events = {
    click: {
      '@records-type':         'handleRecordsType',        // type 탭
      '@bulletin-error-save':  'handleAssembleErrorSave',  // 버그 칸 textarea 글로
      '@bulletin-error-clear': 'handleAssembleErrorClear', // 버그 칸 null 로
    },
  };

  constructor(args: { vendor: string; doc_no: number }) {
    super();
    assert(isVendor(args.vendor), `주소창의 vendor 는 E_VENDORS 중 하나여야 한다. 받은 값: ${args.vendor}`);
    assert(Number.isInteger(args.doc_no) && args.doc_no > 0, `주소창의 doc_no 는 양의 정수여야 한다. 받은 값: ${args.doc_no}`);

    this.data = { key: { vendor: args.vendor, doc_no: args.doc_no }, doc: null, records: [], typeFilter: 'all', loading: true, error: '' };
  }

  override getTitle(): string {
    return `불레틴 ${this.data.key.doc_no} 레코드 — HCLTech 어드민`;
  }

  override onMount(): void {
    void this.loadRecords();
  }

  render(): HTMLElement {
    return htmlSolaWithInstance(this)`
      <div class="page page-bulletin-records">
        <div class="br-body">${this.renderBody()}</div>
      </div>
    `;
  }

  // ===== 핸들러 — 던지면 Sola 의 onActionError(alert) 가 받는다 =====

  /** type 탭 — typeFilter 를 바꾸고 다시 그린다(서버 안 부름) */
  handleRecordsType(_e: Event, el: HTMLElement): void {
    const filter = el.dataset.type;
    assert(isRecordTypeFilter(filter), `type 탭의 data-type 은 E_RECORD_TYPE_FILTERS 중 하나여야 한다 — 탭은 그 배열로 그렸다. 받은 값: ${filter}`);

    this.data = { ...this.data, typeFilter: filter };
    this.rerenderBody();
  }

  /** 버그 칸 저장 — textarea 글을 setAssembleError 로. 빈 글이면 안내만 */
  async handleAssembleErrorSave(): Promise<void> {
    const why = this.readWhy();
    if (why === '') {
      alert('버그 칸이 비어 있습니다. 비우려면 [비우기] 를 누르세요.');
      return;
    }

    const saved = await api.setAssembleError(this.data.key, why);
    this.replaceDoc(saved);
  }

  /** 버그 칸 비우기 — setAssembleError(null) */
  async handleAssembleErrorClear(): Promise<void> {
    const cleared = await api.setAssembleError(this.data.key, null);
    this.replaceDoc(cleared);
  }

  // ===== 로드 · 재렌더 =====

  /** 그 편(fetchBulletinDoc)과 레코드(fetchAssembledRecords)를 같이 받는다. 어느 하나라도 실패하면 error */
  private async loadRecords(): Promise<void> {
    try {
      const [doc, records] = await Promise.all([api.fetchBulletinDoc(this.data.key), api.fetchAssembledRecords(this.data.key)]);
      this.data = { ...this.data, doc, records, loading: false, error: '' };
    } catch (err) {
      this.data = { ...this.data, loading: false, error: err instanceof Error ? err.message : '불러오기 실패' };
    }
    this.rerenderBody();
  }

  /** 서버가 돌려준 그 편으로 doc 을 바꾼다 */
  private replaceDoc(doc: api.BulletinDocView): void {
    this.data = { ...this.data, doc };
    this.rerenderBody();
  }

  private readWhy(): string {
    const box = this._rootElement?.querySelector<HTMLTextAreaElement>('textarea.br-why');
    assert(box, 'assemble_error textarea 는 편이 그려질 때 같이 그려진다 — 저장 버튼이 눌렸으면 있어야 한다');

    return box.value.trim();
  }

  private renderBody(): string {
    if (this.data.loading)      return `<div class="text-muted br-note">불러오는 중…</div>`;
    if (this.data.error)        return `<div class="ce-alert">${this.data.error}</div>`;
    if (this.data.doc === null) return `<div class="text-muted br-note">편이 없습니다.</div>`;

    return `
      ${renderRecordsHead(this.data)}
      ${renderTypeTabs(this.data)}
      ${renderRecords(this.data)}
      ${renderAssembleError(this.data.doc)}`;
  }

  private rerenderBody(): void {
    this.refreshRegion('.br-body', () => this.renderBody());
  }
}
